import { useState, useMemo } from 'react';
import { usePipeline } from '../../context/PipelineContext';
import {
  PIPELINE_STAGES, DIVISION_STAGES, LOSS_REASONS, SERVICE_TIERS,
  calculateConversionRates, calculateStageVelocity,
} from '../../utils/pipelineCalculations';
import { formatPercent } from '../../utils/formatting';
import SectionLabel from '../common/SectionLabel';

const REASON_COLORS = {
  unresponsive: '#FF6E83',
  price: '#FF9B99',
  unqualified: '#c59000',
  competitor: '#022889',
  timing: '#3EAAD0',
  other: '#888888',
};

const DIVISION_OPTIONS = [
  { key: 'all', label: 'All Divisions' },
  { key: 'bwm', label: 'BWM' },
  { key: 'bts', label: 'BTS' },
  { key: 'bia', label: 'BIA' },
];

function getProspectDivision(p) {
  if (p.division) return p.division;
  const tier = SERVICE_TIERS[p.serviceTier];
  return tier?.division || 'bwm';
}

export default function ConversionFunnel() {
  const { prospects } = usePipeline();
  const [divisionFilter, setDivisionFilter] = useState('all');

  // Filter prospects by division
  const filteredProspects = useMemo(() => {
    if (divisionFilter === 'all') return prospects || [];
    return (prospects || []).filter(p => getProspectDivision(p) === divisionFilter);
  }, [prospects, divisionFilter]);

  // Get the stages relevant to this division filter
  const divisionForStages = divisionFilter !== 'all' ? divisionFilter : null;
  const activeStageIds = divisionForStages
    ? DIVISION_STAGES[divisionForStages] || DIVISION_STAGES.bwm
    : ['discovery-scheduled', 'discovery-complete', 'strategy-scheduled', 'strategy-complete', 'proposal'];
  const activeStages = activeStageIds.map(id => PIPELINE_STAGES.find(s => s.id === id)).filter(Boolean);

  // Calculate conversion rates and velocity for the filtered set
  const conversionRates = useMemo(
    () => calculateConversionRates(filteredProspects, divisionForStages),
    [filteredProspects, divisionForStages]
  );
  const stageVelocity = useMemo(
    () => calculateStageVelocity(filteredProspects, divisionForStages),
    [filteredProspects, divisionForStages]
  );

  // Count prospects that entered each stage
  const stageCounts = useMemo(() => {
    const counts = {};
    for (const stage of activeStages) {
      counts[stage.id] = filteredProspects.filter(p =>
        p.stageHistory && p.stageHistory.some(h => h.stageId === stage.id)
      ).length;
    }
    return counts;
  }, [filteredProspects, activeStages]);

  const maxCount = Math.max(...Object.values(stageCounts), 1);

  // Build funnel data
  const funnelData = useMemo(() => {
    return activeStages.map((stage, idx) => {
      const count = stageCounts[stage.id] || 0;
      const widthPct = Math.max((count / maxCount) * 100, 8);

      let conversionToNext = null;
      if (idx < activeStages.length - 1) {
        conversionToNext = conversionRates.find(c => c.fromStageId === stage.id) || null;
      }

      return { stage, count, widthPct, conversionToNext };
    });
  }, [activeStages, stageCounts, maxCount, conversionRates]);

  // Loss reason breakdown (filtered by division)
  const lossReasonCounts = useMemo(() => {
    const lost = filteredProspects.filter(p => p.stage === 'closed-lost' || p.stage === 'unqualified');
    if (lost.length === 0) return [];

    const counts = {};
    for (const p of lost) {
      const reason = p.lossReason || 'other';
      const reasonDef = LOSS_REASONS.find(r => r.id === reason || r.label === reason);
      const key = reasonDef?.id || 'other';
      counts[key] = (counts[key] || 0) + 1;
    }

    return LOSS_REASONS
      .map(r => ({ id: r.id, label: r.label, count: counts[r.id] || 0 }))
      .filter(r => r.count > 0)
      .sort((a, b) => b.count - a.count);
  }, [filteredProspects]);

  const totalLost = lossReasonCounts.reduce((s, r) => s + r.count, 0);
  const maxReasonCount = lossReasonCounts.length > 0 ? Math.max(...lossReasonCounts.map(r => r.count)) : 1;

  return (
    <div>
      {/* Header with division filter */}
      <div className="flex items-center justify-between mb-4">
        <SectionLabel>Conversion Funnel</SectionLabel>
        <div className="flex gap-0.5 bg-gray-100 rounded-[4px] p-0.5">
          {DIVISION_OPTIONS.map((opt) => (
            <button
              key={opt.key}
              onClick={() => setDivisionFilter(opt.key)}
              className={`px-3 py-1.5 text-[11px] font-bold rounded-[3px] transition-colors ${
                divisionFilter === opt.key
                  ? 'bg-white text-navy shadow-sm'
                  : 'text-gray-400 hover:text-gray-600'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {/* Funnel visualization */}
      <div className="bg-white rounded-[4px] shadow-[0_2px_6px_rgba(0,0,0,0.07)] p-5 mb-5">
        {divisionFilter !== 'all' && (
          <p className="text-[11px] font-light text-[#888] mb-3">
            Showing {activeStages.length}-stage pipeline for {DIVISION_OPTIONS.find(o => o.key === divisionFilter)?.label}
          </p>
        )}
        <div className="space-y-1">
          {funnelData.map((item, idx) => {
            const opacity = 1 - (idx * (0.6 / Math.max(activeStages.length - 1, 1)));
            return (
              <div key={item.stage.id}>
                <div className="flex items-center gap-3">
                  <div className="w-full">
                    <div
                      className="rounded-[4px] px-3 py-2.5 flex items-center justify-between transition-all duration-300 mx-auto"
                      style={{
                        width: `${item.widthPct}%`,
                        backgroundColor: `rgba(0, 109, 120, ${opacity})`,
                      }}
                    >
                      <span className="text-white text-[13px] font-bold truncate">
                        {item.stage.label}
                      </span>
                      <span className="text-white/90 text-[12px] font-bold shrink-0 ml-2">
                        {item.count}
                      </span>
                    </div>
                  </div>
                </div>

                {idx < funnelData.length - 1 && (
                  <div className="flex items-center gap-2 py-1 pl-4">
                    <svg width="12" height="16" viewBox="0 0 12 16" className="shrink-0 text-deep-teal/50">
                      <path d="M6 0 L6 12 L2 8 M6 12 L10 8" stroke="currentColor" fill="none" strokeWidth="1.5" />
                    </svg>
                    {item.conversionToNext ? (
                      item.conversionToNext.rate !== null ? (
                        <div className="flex items-center gap-3 text-[11px]">
                          <span className="text-deep-teal font-bold">
                            {formatPercent(item.conversionToNext.rate)} advance
                          </span>
                          <span className="text-rose font-bold">
                            {formatPercent(1 - item.conversionToNext.rate)} drop off
                          </span>
                          <span className="text-[#aaa]">(n={item.conversionToNext.sampleSize})</span>
                        </div>
                      ) : (
                        <span className="text-[11px] text-[#aaa] italic">
                          Insufficient data (n={item.conversionToNext.sampleSize})
                        </span>
                      )
                    ) : (
                      <span className="text-[11px] text-[#aaa] italic">Insufficient data</span>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Drop-Off Analysis */}
      {lossReasonCounts.length > 0 && (
        <div className="bg-white rounded-[4px] shadow-[0_2px_6px_rgba(0,0,0,0.07)] p-5 mb-5">
          <div className="text-[11px] font-bold uppercase tracking-[0.12em] text-deep-teal mb-3">
            Drop-Off Analysis {divisionFilter !== 'all' ? `(${DIVISION_OPTIONS.find(o => o.key === divisionFilter)?.label})` : ''}
          </div>
          <div className="space-y-2">
            {lossReasonCounts.map(reason => {
              const barWidth = (reason.count / maxReasonCount) * 100;
              return (
                <div key={reason.id} className="flex items-center gap-3">
                  <span className="text-[12px] text-navy w-32 shrink-0 text-right">{reason.label}</span>
                  <div className="flex-1 flex items-center gap-2">
                    <div className="flex-1 h-5 bg-[#f0f0f0] rounded-[3px] overflow-hidden">
                      <div
                        className="h-full rounded-[3px] transition-all duration-300"
                        style={{ width: `${barWidth}%`, backgroundColor: REASON_COLORS[reason.id] || '#888' }}
                      />
                    </div>
                    <span className="text-[12px] font-bold text-navy w-8 text-right">{reason.count}</span>
                    <span className="text-[11px] text-[#888] w-12 text-right">{formatPercent(reason.count / totalLost)}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Stage Velocity */}
      <div className="bg-white rounded-[4px] shadow-[0_2px_6px_rgba(0,0,0,0.07)] p-5">
        <div className="text-[11px] font-bold uppercase tracking-[0.12em] text-deep-teal mb-3">
          Stage Velocity {divisionFilter !== 'all' ? `(${DIVISION_OPTIONS.find(o => o.key === divisionFilter)?.label})` : ''}
        </div>
        <div className={`grid gap-3`} style={{ gridTemplateColumns: `repeat(${stageVelocity.length || 1}, 1fr)` }}>
          {(stageVelocity || []).map(sv => {
            let colorClass = 'text-[#888]';
            let bgClass = 'bg-[#f5f5f5]';
            if (sv.avgDays !== null) {
              if (sv.avgDays <= 7) { colorClass = 'text-deep-teal'; bgClass = 'bg-deep-teal/5'; }
              else if (sv.avgDays <= 14) { colorClass = 'text-[#c59000]'; bgClass = 'bg-[#c59000]/5'; }
              else { colorClass = 'text-rose'; bgClass = 'bg-rose/5'; }
            }
            return (
              <div key={sv.stageId} className={`${bgClass} rounded-[4px] p-3 text-center`}>
                <div className="text-[10px] font-bold uppercase tracking-[0.1em] text-navy/60 mb-1 truncate">{sv.label}</div>
                <div className={`text-[24px] font-bold leading-tight ${colorClass}`}>
                  {sv.avgDays !== null ? sv.avgDays : '--'}
                </div>
                <div className="text-[10px] text-[#aaa]">{sv.avgDays !== null ? 'avg days' : 'no data'}</div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
